import { useEffect, useRef } from 'react';

type UseInfiniteScrollOptions = {
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
    /** Distance before sentinel enters view to prefetch next page */
    rootMargin?: string;
};

/**
 * Observes a sentinel element and calls `onLoadMore` when it nears the viewport.
 * Re-attaches when loading finishes so the sentinel (mounted after first fetch) is observed.
 */
export function useInfiniteScroll({
    onLoadMore,
    hasMore,
    isLoading,
    isLoadingMore,
    rootMargin = '320px'
}: UseInfiniteScrollOptions) {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const onLoadMoreRef = useRef(onLoadMore);
    const hasMoreRef = useRef(hasMore);
    const isBusyRef = useRef(isLoading || isLoadingMore);
    const inFlightRef = useRef(false);

    onLoadMoreRef.current = onLoadMore;
    hasMoreRef.current = hasMore;
    isBusyRef.current = isLoading || isLoadingMore;

    useEffect(() => {
        if (!isLoading && !isLoadingMore) {
            inFlightRef.current = false;
        }
    }, [isLoading, isLoadingMore]);

    useEffect(() => {
        if (!hasMore || isLoading || isLoadingMore) {
            return;
        }

        const el = sentinelRef.current;
        if (!el) return;

        const tryLoadMore = () => {
            if (!hasMoreRef.current || isBusyRef.current || inFlightRef.current) return;
            inFlightRef.current = true;
            onLoadMoreRef.current();
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                tryLoadMore();
            },
            { root: null, rootMargin, threshold: 0 }
        );

        observer.observe(el);

        return () => observer.disconnect();
    }, [hasMore, isLoading, isLoadingMore, rootMargin]);

    return sentinelRef;
}
